import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useSupabase } from '../../hooks/useSupabase';
import MetricCard from './components/MetricCard';
import ActivityFeed from './components/ActivityFeed';
import QuickActionTiles from './components/QuickActionTiles';
import NotificationPanel from './components/NotificationPanel';
import DateRangeSelector from './components/DateRangeSelector';
import ExportPanel from './components/ExportPanel';

const AdministrativeDashboard = () => {
  const navigate = useNavigate();
  const { supabase } = useSupabase();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [dashboardData, setDashboardData] = useState({
    currentClockedIn: 0,
    totalDailyPunches: 0,
    missedClockIns: 0,
    avgAttendance: 0
  });
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Real dashboard data is now loaded from database

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Load dashboard data from database
  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const today = new Date();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

      // Get currently clocked in employees (have clock_in_time but no clock_out_time today)
      const { data: clockedInData, error: clockedInError } = await supabase
        .from('time_entries')
        .select(`
          id,
          employee_id,
          employees!inner(
            id,
            name,
            department
          )
        `)
        .gte('clock_in_time', startOfDay.toISOString())
        .lte('clock_in_time', endOfDay.toISOString())
        .is('clock_out_time', null);

      if (clockedInError) throw clockedInError;

      // Get total daily punches (all time entries for today)
      const { data: totalPunchesData, error: totalPunchesError } = await supabase
        .from('time_entries')
        .select('id')
        .gte('clock_in_time', startOfDay.toISOString())
        .lte('clock_in_time', endOfDay.toISOString());

      if (totalPunchesError) throw totalPunchesError;

      // Get all active employees to calculate missed clock-ins
      const { data: allEmployeesData, error: allEmployeesError } = await supabase
        .from('employees')
        .select('id, department')
        .eq('is_active', true);

      if (allEmployeesError) throw allEmployeesError;

      // Get employees who clocked in today
      const { data: clockedInTodayData, error: clockedInTodayError } = await supabase
        .from('time_entries')
        .select('employee_id')
        .gte('clock_in_time', startOfDay.toISOString())
        .lte('clock_in_time', endOfDay.toISOString());

      if (clockedInTodayError) throw clockedInTodayError;

      const clockedInEmployeeIds = new Set(clockedInTodayData?.map(entry => entry.employee_id) || []);
      const missedClockIns = allEmployeesData?.filter(emp => !clockedInEmployeeIds.has(emp.id))?.length || 0;

      // Department statistics removed as requested

      // Calculate average attendance percentage
      const totalEmployees = allEmployeesData?.length || 0;
      const totalPresent = clockedInData?.length || 0;
      const avgAttendance = totalEmployees > 0 ? Math.round((totalPresent / totalEmployees) * 100 * 10) / 10 : 0;

      setDashboardData({
        currentClockedIn: clockedInData?.length || 0,
        totalDailyPunches: totalPunchesData?.length || 0,
        missedClockIns,
        avgAttendance
      });

    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
  }, [selectedDateRange]);

  const handleDateRangeChange = (dateRange) => {
    setSelectedDateRange(dateRange);
    // In a real application, this would trigger a data refresh
    console.log('Date range changed:', dateRange);
  };

  const handleKioskMode = () => {
    navigate('/kiosk-clock-interface');
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const formatDate = (date) => {
    return date?.toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Notification counts for header
  const notificationCounts = {
    orders: 0,
    technicians: 0
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header userRole="admin" notificationCounts={notificationCounts} />
        {/* Hide sidebar on mobile */}
        <div className="hidden lg:block">
          <Sidebar 
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            userRole="admin"
            notificationCounts={notificationCounts}
          />
        </div>
        <main className={`transition-all duration-200 ease-out ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        } pt-16`}>
          <div className="flex">
            <div className="flex-1 p-6 lg:pr-0">
              <div className="max-w-none">
                <div className="animate-pulse space-y-8">
                  <div className="h-8 bg-muted rounded w-1/3"></div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[1, 2, 3, 4]?.map((i) => (
                      <div key={i} className="h-32 bg-muted rounded-lg"></div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 h-96 bg-muted rounded-lg"></div>
                    <div className="h-96 bg-muted rounded-lg"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="admin" notificationCounts={notificationCounts} />
      {/* Hide sidebar on mobile */}
      <div className="hidden lg:block">
        <Sidebar 
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          userRole="admin"
          notificationCounts={notificationCounts}
        />
      </div>
      <main className={`transition-all duration-200 ease-out ${
        isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      } pt-16`}>
        <div className="flex">
          <div className="flex-1 p-6 lg:pr-0">
            <div className="max-w-none">
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-text-primary mb-2">
                Administratives Dashboard
              </h1>
              <div className="flex items-center space-x-4 text-text-secondary">
                <div className="flex items-center space-x-2">
                  <Icon name="Calendar" size={16} />
                  <span>{formatDate(currentTime)}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={16} />
                  <span>{formatTime(currentTime)}</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button
                variant="outline"
                onClick={loadDashboardData}
                iconName="RefreshCw"
                iconPosition="left"
                className="touch-feedback"
                disabled={isLoading}
              >
                Aktualisieren
              </Button>
              <Button
                variant="outline"
                onClick={handleKioskMode}
                iconName="Monitor"
                iconPosition="left"
                className="touch-feedback"
              >
                Zum Kiosk wechseln
              </Button>
              <Button
                variant="default"
                onClick={() => navigate('/employee-management?action=add')}
                iconName="UserPlus"
                iconPosition="left"
                className="touch-feedback"
              >
                Mitarbeiter hinzufügen
              </Button>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <span className="text-error font-medium">{error}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setError(null)}
                  className="ml-auto"
                >
                  <Icon name="X" size={16} />
                </Button>
              </div>
            </div>
          )}

          {/* Summary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-8">
            <MetricCard
              title="Aktuell eingestempelt"
              value={dashboardData?.currentClockedIn}
              icon="Users"
              color="success"
              trend="up"
              trendValue="+5"
              onClick={() => navigate('/employee-management?filter=active')}
            />
            <MetricCard
              title="Gesamt Stempelungen heute"
              value={dashboardData?.totalDailyPunches}
              icon="Clock"
              color="primary"
              trend="up"
              trendValue="+12"
              onClick={() => navigate('/time-entry-corrections')}
            />
            {/* <MetricCard
              title="Fehlende Einstempelungen"
              value={dashboardData?.missedClockIns}
              icon="AlertTriangle"
              color="error"
              trend="down"
              trendValue="-2"
              onClick={() => navigate('/time-entry-corrections')}
            />
            <MetricCard
              title="Durchschnitt Anwesenheit"
              value={`${dashboardData?.avgAttendance || 0}%`}
              icon="TrendingUp"
              color="warning"
              trend="up"
              trendValue="+1.2%"
              onClick={() => {}}
            /> */}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            {/* Activity Feed - Takes 2 columns */}
            <div className="lg:col-span-3">
              <ActivityFeed />
            </div>
            
            {/*
            <div>
              <NotificationPanel />
            </div> */}
          </div>

          {/* Quick Action Tiles */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-6">
              Verwaltungsfunktionen
            </h2>
            <QuickActionTiles />
          </div>

          {/* Department Statistics removed as requested */}

          {/* Bottom Panel - Date Range and Export */}
          {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <DateRangeSelector onDateRangeChange={handleDateRangeChange} />
            
          </div> */}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdministrativeDashboard;